import { useMediaQuery } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalViews } from '../../../constants';
import { corporateBrandNames } from '../../../constants/brandMapping';
import { AccessRequestItem, RequestStatusType } from '../../../hooks/useUserPermissions';
import { Icons, Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { IconWithText, Modal } from '../../../stories/molecules';
import { muiTheme } from '../../../theme';
import { commonTranslations } from '../../../translations';
import { accessRequestTranslations } from '../../../translations/accessRequest';
import { AccessStateView } from '../accessStateView/AccessStateView';
import { StatusTableitem } from '../statusTableItem/StatusTableItem';
import { StyledButton, StyledTypography } from './ManageAccessModal.styles';

type ButtonConfig = {
  label: string;
  icon: Icons;
  action?: RequestStatusType;
  variant?: 'primary' | 'secondary';
  onClick: () => void;
};

type AccessDetailRowProps = {
  label: string;
  value: string | React.ReactNode;
};

export const AccessDetailRow: React.FC<AccessDetailRowProps> = ({ label, value }) => {
  const isString = typeof value === 'string';
  return (
    <RowFlexContainer padding={[2, 0]} justifyContent="between" width="100%">
      <StyledTypography>{label}</StyledTypography>
      {isString ? <Typography>{value}</Typography> : value}
    </RowFlexContainer>
  );
};

export type ManageAccessModalProps = {
  isOpen: boolean;
  requestAction: RequestStatusType;
  selectedItem: AccessRequestItem;
  isLoading: boolean;
  modalView: ModalViews;
  onClose: () => void;
  sendRequest: (requestId: string, updatedStatus: RequestStatusType) => void;
};

export const ManageAccessModal: FC<ManageAccessModalProps> = ({
  isOpen,
  requestAction,
  selectedItem,
  isLoading,
  modalView,
  onClose,
  sendRequest,
}) => {
  const { t } = useTranslation();
  const { manageAccessModal: manageAccessModalTranslations } = accessRequestTranslations;
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));

  const [clickedAction, setClickedAction] = useState<RequestStatusType | null>(null);

  const handleRequest = (action: RequestStatusType) => {
    setClickedAction(action);
    sendRequest(selectedItem.id, action);
  };

  const actionButtonMap: Record<Exclude<RequestStatusType, 'Expired'>, ButtonConfig[]> = {
    Pending: [
      {
        label: t(
          manageAccessModalTranslations.rejectAccess.key,
          manageAccessModalTranslations.rejectAccess.defaultValue,
        ),
        icon: Icons.CLOSE,
        action: 'Rejected',
        onClick: () => handleRequest('Rejected'),
      },
      {
        label: t(
          manageAccessModalTranslations.approveAccess.key,
          manageAccessModalTranslations.approveAccess.defaultValue,
        ),
        icon: Icons.CHECK_MARK,
        action: 'Approved',
        onClick: () => handleRequest('Approved'),
      },
    ],
    Approved: [
      {
        label: t(commonTranslations.cancel.key, commonTranslations.cancel.defaultValue),
        icon: Icons.CLOSE,
        onClick: onClose,
        variant: 'secondary',
      },
      {
        label: t(
          manageAccessModalTranslations.revokeAccess.key,
          manageAccessModalTranslations.revokeAccess.defaultValue,
        ),
        icon: Icons.CLOSE,
        onClick: () => {},
      },
    ],
    Rejected: [
      {
        label: t(commonTranslations.cancel.key, commonTranslations.cancel.defaultValue),
        icon: Icons.CLOSE,
        onClick: onClose,
        variant: 'secondary',
      },
      {
        label: t(
          manageAccessModalTranslations.approveAccess.key,
          manageAccessModalTranslations.approveAccess.defaultValue,
        ),
        icon: Icons.CHECK_MARK,
        onClick: () => {},
      },
    ],
  };

  const currentButtons = actionButtonMap[requestAction as keyof typeof actionButtonMap];

  const getAccessViewText = (modaltype: ModalViews, action: RequestStatusType | null) => {
    const isApproved = action === 'Approved';

    if (modaltype === ModalViews.SUCCESS) {
      return isApproved
        ? t(
            manageAccessModalTranslations.approvedSuccessText.key,
            manageAccessModalTranslations.approvedSuccessText.defaultValue,
          )
        : t(
            manageAccessModalTranslations.rejectedSuccessText.key,
            manageAccessModalTranslations.rejectedSuccessText.defaultValue,
          );
    } else if (modaltype === ModalViews.ERROR) {
      return isApproved
        ? t(
            manageAccessModalTranslations.approvedErrorText.key,
            manageAccessModalTranslations.approvedErrorText.defaultValue,
          )
        : t(
            manageAccessModalTranslations.rejectedErrorText.key,
            manageAccessModalTranslations.rejectedErrorText.defaultValue,
          );
    } else {
      return '';
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <ColumnFlexContainer
        data-testid="manage-access-modal-container"
        padding={isTablet ? [6] : [8]}
        width={isTablet ? '' : '600px'}
      >
        {modalView === ModalViews.INITIAL && (
          <>
            <Typography type="h4" gutterBottom={isTablet}>
              {requestAction === 'Pending'
                ? t(
                    manageAccessModalTranslations.reviewRequest.key,
                    manageAccessModalTranslations.reviewRequest.defaultValue,
                  )
                : t(
                    manageAccessModalTranslations.modifyRequest.key,
                    manageAccessModalTranslations.modifyRequest.defaultValue,
                  )}
            </Typography>
            <IconWithText
              data-testid="employee-upn"
              label={selectedItem.upn}
              iconOptions={{
                icon: Icons.EMAIL,
                size: 'small',
              }}
              position="left"
            />

            <ColumnFlexContainer
              padding={[3]}
              margin={[4, 0, 0]}
              borderRadius={[2]}
              hasBorder
              data-testid="modal-content"
            >
              <AccessDetailRow
                label={t(
                  manageAccessModalTranslations.requestID.key,
                  manageAccessModalTranslations.requestID.defaultValue,
                )}
                value={selectedItem.id}
              />
              <AccessDetailRow
                label={t(manageAccessModalTranslations.role.key, manageAccessModalTranslations.role.defaultValue)}
                value={selectedItem.requestedAssignment.title ? selectedItem.requestedAssignment.title : '-'}
              />
              <AccessDetailRow
                label={t(
                  manageAccessModalTranslations.organization.key,
                  manageAccessModalTranslations.organization.defaultValue,
                )}
                value={
                  selectedItem.requestedAssignment.organizationName
                    ? selectedItem.requestedAssignment.organizationName
                    : '-'
                }
              />
              <AccessDetailRow
                label={t(
                  manageAccessModalTranslations.businessUnit.key,
                  manageAccessModalTranslations.businessUnit.defaultValue,
                )}
                value={
                  selectedItem.requestedAssignment.businessUnitName
                    ? selectedItem.requestedAssignment.businessUnitName
                    : '-'
                }
              />
              <AccessDetailRow
                label={t(
                  manageAccessModalTranslations.brand.key,
                  manageAccessModalTranslations.brand.defaultValue,
                )}
                value={corporateBrandNames[selectedItem.requestedAssignment.brandId ?? 0]}
              />
              <AccessDetailRow
                label={t(accessRequestTranslations.status.key, accessRequestTranslations.status.defaultValue)}
                value={<StatusTableitem accessRequestItem={selectedItem} position="end" />}
              />
            </ColumnFlexContainer>

            <RowFlexContainer data-testid="button-container" margin={[4, 0, 0]} gap={[2]}>
              {currentButtons.map(({ label, icon, onClick, variant, action }, index) => (
                <StyledButton
                  key={label + index}
                  label={label}
                  aria-haspopup="true"
                  onClick={onClick}
                  variant={variant}
                  isLoading={isLoading && clickedAction === action}
                  buttonContentProps={{
                    iconOptions: {
                      icon,
                      size: 'small',
                    },
                    textOptions: {
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              ))}
            </RowFlexContainer>
          </>
        )}

        {(modalView === ModalViews.SUCCESS || modalView === ModalViews.ERROR) && (
          <AccessStateView
            isSuccess={modalView === ModalViews.SUCCESS}
            message={getAccessViewText(modalView, clickedAction)}
            onClose={onClose}
          />
        )}
      </ColumnFlexContainer>
    </Modal>
  );
};
